import crWordmarkWhite from "@/assets/cipher-row-wordmark-white.svg";
/**
 * LandingNav · shared top nav for marketing pages (/verify, /docs,
 * /blog, /book-demo, etc). Matches the inline nav in src/pages/Index.tsx.
 *
 * Three nav items (Pricing, Docs, Verify a carrier), CR green square
 * logo with "Cipher & Row" wordmark, Sign in + Start free CTAs on the
 * right. Responsive with a mobile menu toggle.
 *
 * Previously this was a 7-item nav with LanguageSwitcher + Live Demo +
 * Integrate + Blog + About and the old hexagon BrandLogo. Simplified
 * to match the new landing page.
 */
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAnime } from "@/lib/anime/useAnime";
import { DURATION, EASE } from "@/lib/anime/motion-vocab";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { CinematicLink } from "@/components/landing/CinematicLink";

export function LandingNav() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const { t } = useTranslation();

  const isActive = (to: string) =>
    pathname === to || pathname.startsWith(to + "/");

  const mobileLinkClass = (to: string) =>
    `block text-[15px] py-2 transition-colors ${
      isActive(to)
        ? "text-primary font-semibold"
        : "text-[#C8C4BD] hover:text-[#F2EDE6]"
    }`;

  const desktopLinkClass = (to: string) =>
    `text-sm transition-colors ${
      isActive(to)
        ? "text-primary font-semibold"
        : "text-[#8A8A91] hover:text-[#F2EDE6]"
    }`;

  const { ref: navRef } = useAnime<HTMLElement>({
    opacity: [0, 1],
    translateY: [-16, 0],
    duration: DURATION.base,
    ease: EASE.soft,
    autoPlay: true,
  });


  return (
    <>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md"
      >
        Skip to content
      </a>
      <nav
        ref={navRef}
        style={{ opacity: 0 }}
        className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between pl-0 pr-2 sm:px-6 md:px-8 py-4 border-b border-[#1C1C1F] bg-[#0A0A0B]/95"
        role="navigation"
        aria-label="Main navigation"
      >
        {/* Logo */}
        <Link to="/" data-cr-brand-logo className="flex items-center -ml-2 sm:ml-0">
          <img
            src={crWordmarkWhite}
            alt="Cipher & Row"
            width={160}
            height={40}
            loading="eager"
            decoding="sync"
            {...({ fetchpriority: "high" } as Record<string, string>)}
            className="h-10 shrink-0 object-contain"
          />
        </Link>

        {/* Desktop nav links */}
        <div className="hidden md:flex items-center gap-8">
          <CinematicLink
            to="/pricing"
            aria-current={isActive("/pricing") ? "page" : undefined}
            className={desktopLinkClass("/pricing")}
          >
            {t("nav.pricing")}
          </CinematicLink>
          <CinematicLink
            to="/coverage"
            aria-current={isActive("/coverage") ? "page" : undefined}
            className={desktopLinkClass("/coverage")}
          >
            {t("nav.coverage")}
          </CinematicLink>
          <CinematicLink
            to="/docs"
            aria-current={isActive("/docs") ? "page" : undefined}
            className={desktopLinkClass("/docs")}
          >
            {t("nav.docs")}
          </CinematicLink>
          <CinematicLink
            to="/blog"
            aria-current={isActive("/blog") ? "page" : undefined}
            className={desktopLinkClass("/blog")}
          >
            {t("nav.blog")}
          </CinematicLink>
          <CinematicLink
            to="/about"
            aria-current={isActive("/about") ? "page" : undefined}
            className={desktopLinkClass("/about")}
          >
            {t("nav.about")}
          </CinematicLink>
          <CinematicLink
            to="/verify"
            aria-current={isActive("/verify") ? "page" : undefined}
            className={desktopLinkClass("/verify")}
          >
            {t("nav.verifyCarrier")}
          </CinematicLink>
          <CinematicLink
            to="/brokers/signup"
            aria-current={isActive("/brokers") ? "page" : undefined}
            className={desktopLinkClass("/brokers/signup")}
          >
            {t("nav.forBrokers")}
          </CinematicLink>
        </div>

        {/* Desktop CTAs */}
        <div className="hidden md:flex items-center gap-3">
          <LanguageSwitcher variant="icon" />
          <Link
            to="/login"
            aria-current={isActive("/login") ? "page" : undefined}
            className={desktopLinkClass("/login")}
          >
            {t("nav.signIn")}
          </Link>
          <Link
            to="/signup"
            className="px-4 py-2 rounded-lg bg-primary text-[#0A0A0B] text-sm font-semibold hover:bg-primary/90 transition-colors"
          >
            {t("nav.startFree")}
          </Link>
        </div>

        {/* Mobile menu toggle */}
        <button
          type="button"
          className="md:hidden text-[#F2EDE6] min-w-[44px] min-h-[44px] flex items-center justify-center"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden fixed top-[60px] left-0 right-0 z-50 border-b border-[#1C1C1F] bg-[#0A0A0B] p-6 space-y-4">
          <CinematicLink
            to="/pricing"
            onClick={() => setMobileOpen(false)}
            aria-current={isActive("/pricing") ? "page" : undefined}
            className={mobileLinkClass("/pricing")}
          >
            {t("nav.pricing")}
          </CinematicLink>
          <CinematicLink
            to="/coverage"
            onClick={() => setMobileOpen(false)}
            aria-current={isActive("/coverage") ? "page" : undefined}
            className={mobileLinkClass("/coverage")}
          >
            {t("nav.coverage")}
          </CinematicLink>
          <CinematicLink
            to="/docs"
            onClick={() => setMobileOpen(false)}
            aria-current={isActive("/docs") ? "page" : undefined}
            className={mobileLinkClass("/docs")}
          >
            {t("nav.docs")}
          </CinematicLink>
          <CinematicLink
            to="/blog"
            onClick={() => setMobileOpen(false)}
            aria-current={isActive("/blog") ? "page" : undefined}
            className={mobileLinkClass("/blog")}
          >
            {t("nav.blog")}
          </CinematicLink>
          <CinematicLink
            to="/about"
            onClick={() => setMobileOpen(false)}
            aria-current={isActive("/about") ? "page" : undefined}
            className={mobileLinkClass("/about")}
          >
            {t("nav.about")}
          </CinematicLink>
          <CinematicLink
            to="/verify"
            onClick={() => setMobileOpen(false)}
            aria-current={isActive("/verify") ? "page" : undefined}
            className={mobileLinkClass("/verify")}
          >
            {t("nav.verifyCarrier")}
          </CinematicLink>
          <CinematicLink
            to="/brokers/signup"
            onClick={() => setMobileOpen(false)}
            aria-current={isActive("/brokers") ? "page" : undefined}
            className={mobileLinkClass("/brokers/signup")}
          >
            {t("nav.forBrokers")}
          </CinematicLink>
          <Link
            to="/demo-tour"
            onClick={() => setMobileOpen(false)}
            aria-current={isActive("/demo-tour") ? "page" : undefined}
            className={mobileLinkClass("/demo-tour")}
          >
            {t("nav.liveDemo")}
          </Link>
          <div className="pt-4 border-t border-[#1C1C1F] space-y-3">
            <Link
              to="/login"
              onClick={() => setMobileOpen(false)}
              aria-current={isActive("/login") ? "page" : undefined}
              className={mobileLinkClass("/login")}
            >
              {t("nav.signIn")}
            </Link>
            <Link
              to="/signup"
              onClick={() => setMobileOpen(false)}
              className="block w-full text-center px-4 py-3 rounded-lg bg-primary text-[#0A0A0B] text-sm font-semibold"
            >
              {t("nav.startFree")}
            </Link>
            <div className="pt-2 flex items-center justify-center">
              <LanguageSwitcher variant="full" />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
